import { DOCUMENTATION_MENU } from '@modules/private/documentation/data/constants/documentation-catalog.constant'
import { UserRole } from '@data/enums/user-role.enum'

export const MENU_AUTHORIZATION: {
  title: string
  modules: {
    label: string
    icon: string
    routerLink: string
    module: string
    permission: string
    /**
     * Opcional: lista de roles que tienen acceso. Si está presente, el sidebar
     * exige que el rol actual del user esté en la lista ADEMÁS de tener el
     * `permission` declarado. Útil cuando un item compartiría permiso con otros
     * roles (ej. `users.view` lo tienen Admin y Legal) pero solo uno debe
     * verlo en el sidebar (solo Admin gestiona usuarios).
     */
    roles?: UserRole[]
  }[]
}[] = [
  {
    title: 'Home',
    modules: [
      {
        label: 'Dashboard',
        icon: 'fa-solid fa-home',
        routerLink: '/',
        module: 'dashboard',
        permission: 'view',
      },
    ],
  },
  {
    title: 'Insumos',
    modules: [
      {
        label: 'Activos',
        icon: 'fa-brands fa-sellsy',
        routerLink: '/active',
        module: 'assets',
        permission: 'view',
      },
      {
        label: 'Clientes',
        icon: 'fa-solid fa-users-rays',
        routerLink: '/customer',
        module: 'clients',
        permission: 'view',
      },
      {
        label: 'Cotizaciones',
        icon: 'fa-solid fa-calculator',
        routerLink: '/quote',
        module: 'quotations',
        permission: 'view',
      },
      {
        label: 'Proveedores',
        icon: 'fa-solid fa-building',
        routerLink: '/provider',
        module: 'providers',
        permission: 'view',
      },
    ],
  },
  {
    title: 'Cartera',
    modules: [
      {
        label: 'Cartera',
        icon: 'fa-solid fa-wallet',
        routerLink: '/wallet',
        module: 'wallet',
        permission: 'view',
      },
      {
        label: 'Arrendamientos',
        icon: 'fa-solid fa-layer-group',
        routerLink: '/lease',
        module: 'leases',
        permission: 'view',
      },
      /**
       * Una sola puerta. Antes eran tres —Pagos, Facturas y Aplicaciones— que abrían el MISMO
       * componente con tres compuertas distintas, y leído en `roles.json` las tres dejan pasar al
       * mismo conjunto: solo Admin, Operaciones y Finanzas tienen `view`, y lo tienen en los tres
       * módulos. Nadie tiene uno sin los otros dos, así que las tres puertas no protegían a nadie.
       */
      {
        label: 'Cobranza',
        icon: 'fa-solid fa-money-check-dollar',
        //! Apunta al PADRE, no a `/cobranza/dinero`. `p-menu` marca el activo con
        //! `routerLinkActive` por prefijo: con la vista en el enlace, el ítem se apagaba al pasar a
        //! Comprobantes o a «Por atender» y el resaltado saltaba a Dashboard —cuyo `/` sí encaja con
        //! todo—. Una entrada tiene que quedar encendida en sus cinco rutas. `/cobranza` redirige a
        //! `dinero`, así que al hacer clic se llega al mismo sitio.
        routerLink: '/cobranza',
        module: 'payments',
        permission: 'view',
      },
      {
        //* Al PADRE, por la misma razón que Cobranza: `p-menu` marca el activo por prefijo y el ítem
        //* tiene que quedar encendido en Sesiones, Por originar, Activos, Cerrados y `sesion/:id`.
        //* `committee.view` lo tienen Admin, Legal, Operaciones y Finanzas; Legal gestiona desde aquí.
        label: 'Comités',
        icon: 'fa-solid fa-gavel',
        routerLink: '/comites',
        module: 'committee',
        permission: 'view',
      },
      {
        //* Plan v4.2 §9.1 · Legal valida aquí los formatos de buró; Admin la ve en solo lectura.
        label: 'Validaciones de buró',
        icon: 'fa-solid fa-file-signature',
        routerLink: '/credit-history/validations',
        module: 'credit-history',
        permission: 'view-validations',
      },
      {
        //* Plan v4.3 §9.3 · Legal autoriza aquí las preofertas con señales; Admin la ve en solo lectura.
        label: 'Preofertas por autorizar',
        icon: 'fa-solid fa-envelope-open-text',
        routerLink: '/preofertas/legal',
        module: 'lease-preoffer',
        permission: 'view-reviews',
      },
      {
        label: 'Configuración',
        icon: 'fa-solid fa-gear',
        routerLink: '/wallet-config',
        module: 'wallet-config',
        permission: 'view',
      },
      {
        // Compuerta `invoices`, no `wallet-config`: es la pantalla de Contabilidad, y de
        // `wallet-config` no tienen `view`. Ver el comentario de la ruta en app-routing.
        label: 'Reglas de facturación',
        icon: 'fa-solid fa-calendar-check',
        routerLink: '/invoice-rules',
        module: 'invoices',
        permission: 'get-all',
      },
      /*{
          label: 'Reporteria',
          icon: 'fa-solid fa-receipt',
          routerLink: '/active',
          module: 'BUSINESS',
          permission: 'view'
        },*/
    ],
  },
  {
    /**
     * Se llamaba «Cobranza Automatizada» y le cedió el nombre a la pantalla de arriba. Razón
     * técnica, no de gusto: **aquí no se cobra nada, se mandan mensajes** por cuatro canales
     * (EMAIL, SMS, WHATSAPP, CALL). «Cobranza» es el término contable del dinero cobrado y le
     * pertenece a la pantalla que lo enseña. «Campañas» se descartó: la sección CONTIENE un ítem
     * llamado Campañas y la ambigüedad no se iría, se anidaría.
     */
    title: 'Recordatorios',
    modules: [
      {
        label: 'Plantillas',
        icon: 'fa-solid fa-file-lines',
        routerLink: '/dripping/templates',
        module: 'dripping',
        permission: 'view',
      },
      {
        label: 'Estrategias',
        icon: 'fa-solid fa-diagram-project',
        routerLink: '/dripping/strategies',
        module: 'dripping',
        permission: 'view',
      },
      {
        label: 'Campañas',
        icon: 'fa-solid fa-bullhorn',
        routerLink: '/dripping/campaigns',
        module: 'dripping',
        permission: 'view',
      },
      {
        label: 'Comunicaciones',
        icon: 'fa-solid fa-envelope-open-text',
        routerLink: '/dripping/communications',
        module: 'dripping',
        permission: 'view',
      },
    ],
  },
  {
    title: 'Documentación',
    // Un item por manual, derivado del catálogo: agregar un documento no exige tocar
    // este archivo. Las guías de plataforma no se listan — se llega a ellas con el
    // conmutador de la cabecera, junto a su manual.
    modules: [
      ...DOCUMENTATION_MENU.map((doc) => ({
        label: doc.menuLabel,
        icon: doc.icon,
        routerLink: `/documentation/${doc.slug}`,
        module: 'documentation',
        permission: 'view',
      })),
      // La foto global de la revisión: cuántas de las decisiones que le pedimos a
      // Contabilidad están contestadas y cuántas cerramos nosotros.
      {
        label: 'Avance de la revisión',
        icon: 'fa-solid fa-list-check',
        routerLink: '/documentation/avance',
        module: 'documentation',
        permission: 'view',
      },
    ],
  },
  {
    title: 'Sistema',
    modules: [
      {
        label: 'Roadmap',
        icon: 'fa-solid fa-map-location-dot',
        routerLink: '/roadmap',
        module: 'roadmap',
        permission: 'view',
        // Todos los internos (Admin + Operaciones/Finanzas/Contabilidad/Legal/Seller) tienen
        // `roadmap.view` para entrar al «Mapa de trabajo» (Backlog+Tablero). Los sub-tabs
        // Métricas y Metas se ocultan a los no-Admin dentro de la página (RoadmapPageComponent.isAdmin).
        roles: [UserRole.ADMIN, UserRole.OPERACIONES, UserRole.FINANZAS, UserRole.CONTABILIDAD, UserRole.LEGAL, UserRole.SELLER],
      },
      {
        label: 'Administradores',
        icon: 'fa-solid fa-user-tie',
        routerLink: '/admin',
        module: 'users',
        permission: 'view',
        // Solo Admin gestiona usuarios del sistema desde esta pantalla. Legal también tiene
        // `users.view`; el gating explícito por rol evita que vea esta sección.
        roles: [UserRole.ADMIN],
      },
      {
        label: 'Reportería',
        icon: 'fa-solid fa-receipt',
        routerLink: '/admin/reports',
        module: 'reports',
        permission: 'view',
      },
      {
        label: 'Algoritmo de ofertas',
        icon: 'fa-solid fa-sliders',
        routerLink: '/algorithm-config',
        module: 'algorithm-config',
        permission: 'view',
        // Admin edita y activa; Finanzas ve el histórico y simula el impacto, pero no activa:
        // activar cambia las tasas y los montos que la plataforma ofrece desde ese instante.
        // El gating por `roles` es defensa adicional al de módulo/permiso.
        roles: [UserRole.ADMIN, UserRole.FINANZAS],
      },
      {
        //* Plan v4.2 §7.2 · Pesos, umbrales, bandas y vetos de la calculadora. Admin y Finanzas crean y activan (D-17).
        label: 'Calculadora de riesgo',
        icon: 'fa-solid fa-gauge-high',
        routerLink: '/risk-scorecard-config',
        module: 'risk-scorecard-config',
        permission: 'view',
        roles: [UserRole.ADMIN, UserRole.FINANZAS],
      },
      {
        //* Plan v4.3 §9.4 · Umbrales y modo (apagada, sombra, encendida) de la preoferta del canal. Admin y Finanzas (D-62).
        label: 'Preoferta automática',
        icon: 'fa-solid fa-envelope-circle-check',
        routerLink: '/preofertas/configuracion',
        module: 'lease-preoffer-settings',
        permission: 'view',
        roles: [UserRole.ADMIN, UserRole.FINANZAS],
      },
    ],
  },
  {
    title: 'Omnicanal',
    modules: [
      {
        label: 'Métricas',
        icon: 'fa-solid fa-chart-line',
        routerLink: '/omnichannel/metrics',
        module: 'omnichannel',
        permission: 'view',
      },
      // TODO(pipelines): "Bandeja de funnel" OCULTA hasta el módulo de Pipelines.
      // Las notificaciones/tareas del funnel aún no se mapean del todo; se termina de
      // cubrir en Pipelines. Reactivar este ítem (y el <app-notification-bell> del header)
      // cuando arranque ese módulo. El motor de actividades sigue corriendo en backend.
      // {
      //   label: 'Bandeja de funnel',
      //   icon: 'fa-solid fa-list-check',
      //   routerLink: '/funnel-tasks',
      //   module: 'activities',
      //   permission: 'view'
      // },
      {
        label: 'Canales',
        icon: 'fa-solid fa-share-nodes',
        routerLink: '/omnichannel',
        module: 'omnichannel',
        permission: 'create',
      },
    ],
  },
]
